// Запрос «Проверить связь упоминания» (030: contracts/api.md, «Справочник»; data-model.md,
// `directory.link_checks`; research Р7; ASG-RES-05, ASG-ONB-08). «Не мой пункт» открывает запрос
// в «Справочник → Вопросы»; решение — «Это он» (keep), «Другая запись…» (relink), «Только текст»
// (unlink). relink снимает псевдоним, по которому связалось упоминание (Д12), и пишет событие
// directory.links_changed: ответственного в пункте меняет minutes.
import { z } from '../kernel/zod.ts';
import { IdSchema, PersonRefSchema, TimestampSchema, VersionSchema } from '../kernel/common.ts';

/** Предложение «Кто отвечает» текстом (внешнему справочник не виден) — до 200 знаков. */
export const LINK_CHECK_PROPOSED_TEXT_MAX = 200;
/** Пояснение ответственного — до 2000 знаков. */
export const LINK_CHECK_NOTE_MAX = 2000;

export const linkCheckStates = ['open', 'kept', 'relinked', 'unlinked'] as const;
export type LinkCheckState = (typeof linkCheckStates)[number];
export const LinkCheckStateSchema = z.enum(linkCheckStates);

export const linkCheckDecisions = ['keep', 'relink', 'unlink'] as const;
export type LinkCheckDecision = (typeof linkCheckDecisions)[number];
export const LinkCheckDecisionSchema = z.enum(linkCheckDecisions);

export const linkCheckDecisionLabels = {
  keep: 'Это он',
  relink: 'Другая запись…',
  unlink: 'Только текст',
} as const satisfies Record<LinkCheckDecision, string>;

/** GET /api/directory/link-checks?state=open — открытые запросы для «Вопросов»; decided — решённые. */
export const LinkChecksQuerySchema = z.strictObject({
  state: z.enum(['open', 'decided']).optional(),
});
export type LinkChecksQuery = z.input<typeof LinkChecksQuerySchema>;

const RecordRefSchema = z.object({ id: IdSchema, name: z.string() });

/**
 * Строка запроса: «как записано», протокол, раздел, номер пункта, ячейки пункта, запись, с которой
 * связан сейчас, кандидаты и предложение ответственного.
 */
export const LinkCheckRowSchema = z.object({
  id: IdSchema,
  version: VersionSchema,
  state: LinkCheckStateSchema,
  raw: z.string(),
  protocol: z.object({ id: IdSchema, title: z.string() }),
  itemId: IdSchema,
  itemNumber: z.string().nullable(),
  /** Разделы над пунктом — номера и названия, сверху вниз. */
  path: z.array(z.string()),
  cells: z.object({
    title: z.string(),
    dueRaw: z.string().nullable(),
    responsibleRaw: z.string().nullable(),
  }),
  record: RecordRefSchema,
  candidates: z.array(RecordRefSchema.extend({ unit: z.string().nullable() })),
  proposal: z
    .object({ record: RecordRefSchema.nullable(), text: z.string().nullable() })
    .nullable(),
  note: z.string().nullable(),
  reportedBy: PersonRefSchema,
  createdAt: TimestampSchema,
  decision: z
    .object({
      decision: LinkCheckDecisionSchema,
      record: RecordRefSchema.nullable(),
      by: PersonRefSchema,
      at: TimestampSchema,
    })
    .nullable(),
});
export type LinkCheckRow = z.infer<typeof LinkCheckRowSchema>;

export const LinkChecksResponseSchema = z.object({ items: z.array(LinkCheckRowSchema) });
export type LinkChecksResponse = z.infer<typeof LinkChecksResponseSchema>;

/** POST /api/directory/link-checks/{id}/decide: relink — с записью; версия — REC-003. */
export const LinkCheckDecideBodySchema = z
  .strictObject({
    decision: LinkCheckDecisionSchema,
    recordId: IdSchema.optional(),
    version: VersionSchema,
  })
  .refine((body) => (body.decision === 'relink') === (body.recordId !== undefined), {
    message: 'Запись выбирают только для «Другая запись…»',
    path: ['recordId'],
  });
export type LinkCheckDecideBody = z.input<typeof LinkCheckDecideBodySchema>;
